import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  MultiSelect,
  Select,
  ScrollArea,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconDownload,
  IconFile,
  IconHeart,
  IconLink,
  IconPinned,
  IconPinnedOff,
  IconPlus,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';


type RoleRow = {
  id: string;
  name: string;
};

type RessortRow = {
  id: string;
  name: string;
};

type ThreadRow = {
  id: string;
  title: string;
  description: string | null;
  context_type: 'general' | 'scene' | 'song' | 'ensemble';
  context_label: string | null;
  scene_id: string | null;
  scene_name: string | null;
  audience_mode: 'all' | 'targeted';
  role_ids: string[];
  role_names: string[];
  ressort_ids: string[];
  ressort_names: string[];
  post_count: number;
  media_count: number;
  pinned_post: { body?: string | null } | null;
  latest_post: { body?: string | null } | null;
};

type SceneRow = {
  id: string;
  act: number;
  scene_number: number;
  name: string;
};

type CostumeComment = {
  id: string;
  user_id: string;
  comment_text: string;
  created_at: string;
  author_name: string;
};

type CostumeAttachment = {
  id: string;
  type: string;
  bucket_id: string | null;
  object_path: string | null;
  link_url: string | null;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

type PostRow = {
  id: string;
  user_id: string;
  body: string | null;
  external_url: string | null;
  is_pinned: boolean;
  approval_status: 'approved' | 'pending' | 'rejected';
  approval_request_note: string | null;
  approval_requested_at: string | null;
  approval_decided_at: string | null;
  approval_decided_by: string | null;
  approval_comment: string | null;
  created_at: string;
  author_name: string;
  reaction_count: number;
  my_reaction: string | null;
  comment_count: number;
  comments: CostumeComment[];
  attachments: CostumeAttachment[];
};

type CostumeRequestRow = {
  post_id: string;
  thread_id: string;
  thread_title: string;
  audience_mode: 'all' | 'targeted';
  role_names: string[];
  ressort_names: string[];
  user_id: string;
  author_name: string;
  body: string | null;
  external_url: string | null;
  approval_status: 'approved' | 'pending' | 'rejected';
  approval_request_note: string | null;
  approval_comment: string | null;
  approval_requested_at: string | null;
  approval_decided_at: string | null;
  created_at: string;
  attachments: CostumeAttachment[];
};

type MediaRow = {
  attachment_id: string;
  type: string;
  bucket_id: string | null;
  object_path: string | null;
  link_url: string | null;
  file_name: string | null;
  created_at: string;
  author_name: string;
};

type FieldRow = {
  id: string;
  field_key: string;
  label: string;
  field_type: string;
  options: string[];
  is_required: boolean;
  sort_order: number;
  role_ids: string[];
  role_names: string[];
  value?: string | null;
};

type ProfilePayload = {
  can_manage: boolean;
  target_user_id?: string | null;
  target_user_name?: string | null;
  fields: FieldRow[];
};

type ProfileUserRow = {
  user_id: string;
  display_name: string;
  role_ids: string[];
  role_names: string[];
};

type OverviewField = {
  id: string;
  field_key: string;
  label: string;
  field_type: string;
  is_required: boolean;
  sort_order: number;
  role_ids: string[];
  role_names: string[];
};

type OverviewUser = {
  user_id: string;
  display_name: string;
  role_ids: string[];
  role_names: string[];
  values: Record<string, string | null>;
};

type OverviewPayload = {
  fields: OverviewField[];
  users: OverviewUser[];
};

// ── CSV-Export Helper ────────────────────────────────────────────────────────
function _downloadCsv(content: string, filename: string) {
  const bom = '\ufeff'; // BOM für Excel-Kompatibilität
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function _buildProfileCsv(overview: OverviewPayload, roleFilter: string): string {
  const { fields, users } = overview;
  const filtered = roleFilter ? users.filter((u) => u.role_ids.includes(roleFilter)) : users;
  const headers = ['Name', 'Rollen', ...fields.map((f) => f.label)];
  const rows = filtered.map((u) => [
    u.display_name,
    u.role_names.join(' / '),
    ...fields.map((f) => u.values[f.id] ?? ''),
  ]);
  return [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
}

type RolePageRow = {
  id: string;
  title: string;
  body: string;
  sort_order: number;
  is_pinned: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  role_ids: string[];
  role_names: string[];
  can_manage: boolean;
  primary_image_url: string | null;
};

type PollOption = {
  id: string;
  option_text: string;
  sort_order: number;
  vote_count: number;
  selected_by_me: boolean;
  votes: PollVoteDetail[];
};

type PollVoteDetail = {
  user_id: string;
  author_name: string;
  vote_note: string | null;
};

type PollRow = {
  poll_id: string;
  thread_id: string;
  question: string;
  allow_multiple: boolean;
  is_closed: boolean;
  closes_at: string | null;
  created_at: string;
  created_by: string;
  author_name: string;
  can_manage: boolean;
  voter_count: number;
  collect_vote_note: boolean;
  vote_note_label: string;
  my_option_ids: string[];
  my_vote_note: string | null;
  options: PollOption[];
};

function parseApprovalStatus(value: unknown): 'approved' | 'pending' | 'rejected' {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'pending' || raw === 'rejected') return raw;
  return 'approved';
}

function parseContextType(value: unknown): 'general' | 'scene' | 'song' | 'ensemble' {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'scene' || raw === 'song' || raw === 'ensemble') return raw;
  return 'general';
}

function parseAudienceMode(value: unknown): 'all' | 'targeted' {
  return String(value ?? '').trim().toLowerCase() === 'targeted' ? 'targeted' : 'all';
}

function parseThreadRows(raw: unknown): ThreadRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const row = entry as Record<string, unknown>;
    return {
      id: String(row.id ?? '').trim(),
      title: String(row.title ?? '').trim(),
      description: row.description == null ? null : String(row.description),
      context_type: parseContextType(row.context_type),
      context_label: row.context_label == null ? null : String(row.context_label),
      scene_id: row.scene_id == null ? null : String(row.scene_id),
      scene_name: row.scene_name == null ? null : String(row.scene_name),
      audience_mode: parseAudienceMode(row.audience_mode),
      role_ids: Array.isArray(row.role_ids)
        ? row.role_ids.map((x) => String(x)).filter(Boolean)
        : [],
      role_names: Array.isArray(row.role_names)
        ? row.role_names.map((x) => String(x)).filter(Boolean)
        : [],
      ressort_ids: Array.isArray(row.ressort_ids)
        ? row.ressort_ids.map((x) => String(x)).filter(Boolean)
        : [],
      ressort_names: Array.isArray(row.ressort_names)
        ? row.ressort_names.map((x) => String(x)).filter(Boolean)
        : [],
      post_count: Number(row.post_count ?? 0) || 0,
      media_count: Number(row.media_count ?? 0) || 0,
      pinned_post:
        row.pinned_post && typeof row.pinned_post === 'object'
          ? (row.pinned_post as { body?: string | null })
          : null,
      latest_post:
        row.latest_post && typeof row.latest_post === 'object'
          ? (row.latest_post as { body?: string | null })
          : null,
    };
  }).filter((row) => row.id.length > 0);
}

function parseSceneRows(raw: unknown): SceneRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      const id = String(row.id ?? '').trim();
      if (!id) return null;
      return {
        id,
        act: Number(row.act ?? 0),
        scene_number: Number(row.scene_number ?? 0),
        name: String(row.name ?? '').trim(),
      };
    })
    .filter((x): x is SceneRow => !!x);
}

function getThreadContextLabel(thread: ThreadRow): string {
  if (thread.context_type === 'general') return '';
  if (thread.context_type === 'scene') {
    return thread.scene_name || thread.context_label || 'Szene';
  }
  if (thread.context_type === 'song') {
    return thread.context_label || 'Song';
  }
  if (thread.context_type === 'ensemble') return 'Ensemble';
  return '';
}

function buildThreadAudienceLabels(thread: Pick<ThreadRow, 'audience_mode' | 'role_names' | 'ressort_names'>): Array<{ label: string; tone: 'all' | 'role' | 'ressort' }> {
  if (thread.audience_mode === 'all') {
    return [{ label: 'Alle Mitglieder', tone: 'all' }];
  }
  return [
    ...thread.role_names.map((label) => ({ label, tone: 'role' as const })),
    ...thread.ressort_names.map((label) => ({ label, tone: 'ressort' as const })),
  ];
}

function buildThreadAudienceSummary(thread: Pick<ThreadRow, 'audience_mode' | 'role_names' | 'ressort_names'>): string {
  if (thread.audience_mode === 'all') return 'Alle Mitglieder';
  const parts: string[] = [];
  if (thread.role_names.length > 0) parts.push(`Rollen: ${thread.role_names.join(', ')}`);
  if (thread.ressort_names.length > 0) parts.push(`Abteilungen: ${thread.ressort_names.join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : 'Gezielte Gruppe';
}

function parsePostRows(raw: unknown): PostRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const row = entry as Record<string, unknown>;
    const commentsRaw = Array.isArray(row.comments) ? row.comments : [];
    const attachmentsRaw = Array.isArray(row.attachments) ? row.attachments : [];

    const comments: CostumeComment[] = commentsRaw
      .map((c) => {
        if (!c || typeof c !== 'object') return null;
        const map = c as Record<string, unknown>;
        return {
          id: String(map.id ?? '').trim(),
          user_id: String(map.user_id ?? '').trim(),
          comment_text: String(map.comment_text ?? '').trim(),
          created_at: String(map.created_at ?? '').trim(),
          author_name: String(map.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
        };
      })
      .filter((c): c is CostumeComment => !!c && c.id.length > 0);

    const attachments: CostumeAttachment[] = attachmentsRaw
      .map((a) => {
        if (!a || typeof a !== 'object') return null;
        const map = a as Record<string, unknown>;
        return {
          id: String(map.id ?? '').trim(),
          type: String(map.type ?? 'file').trim().toLowerCase(),
          bucket_id: map.bucket_id == null ? null : String(map.bucket_id),
          object_path: map.object_path == null ? null : String(map.object_path),
          link_url: map.link_url == null ? null : String(map.link_url),
          file_name: map.file_name == null ? null : String(map.file_name),
          mime_type: map.mime_type == null ? null : String(map.mime_type),
          size_bytes: map.size_bytes == null ? null : Number(map.size_bytes),
          created_at: String(map.created_at ?? '').trim(),
        };
      })
      .filter((a): a is CostumeAttachment => !!a && a.id.length > 0);

    return {
      id: String(row.id ?? '').trim(),
      user_id: String(row.user_id ?? '').trim(),
      body: row.body == null ? null : String(row.body),
      external_url: row.external_url == null ? null : String(row.external_url),
      is_pinned: row.is_pinned === true,
      approval_status: parseApprovalStatus(row.approval_status),
      approval_request_note:
        row.approval_request_note == null ? null : String(row.approval_request_note),
      approval_requested_at:
        row.approval_requested_at == null ? null : String(row.approval_requested_at),
      approval_decided_at:
        row.approval_decided_at == null ? null : String(row.approval_decided_at),
      approval_decided_by:
        row.approval_decided_by == null ? null : String(row.approval_decided_by),
      approval_comment: row.approval_comment == null ? null : String(row.approval_comment),
      created_at: String(row.created_at ?? '').trim(),
      author_name: String(row.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
      reaction_count: Number(row.reaction_count ?? 0) || 0,
      my_reaction: row.my_reaction == null ? null : String(row.my_reaction),
      comment_count: Number(row.comment_count ?? comments.length) || comments.length,
      comments,
      attachments,
    };
  }).filter((row) => row.id.length > 0);
}

function parseRequestRows(raw: unknown): CostumeRequestRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      const postId = String(row.post_id ?? '').trim();
      if (!postId) return null;

      const attachmentsRaw = Array.isArray(row.attachments) ? row.attachments : [];
      const attachments: CostumeAttachment[] = attachmentsRaw
        .map((a) => {
          if (!a || typeof a !== 'object') return null;
          const map = a as Record<string, unknown>;
          return {
            id: String(map.id ?? '').trim(),
            type: String(map.type ?? 'file').trim().toLowerCase(),
            bucket_id: map.bucket_id == null ? null : String(map.bucket_id),
            object_path: map.object_path == null ? null : String(map.object_path),
            link_url: map.link_url == null ? null : String(map.link_url),
            file_name: map.file_name == null ? null : String(map.file_name),
            mime_type: map.mime_type == null ? null : String(map.mime_type),
            size_bytes: map.size_bytes == null ? null : Number(map.size_bytes),
            created_at: String(map.created_at ?? '').trim(),
          };
        })
        .filter((x): x is CostumeAttachment => !!x && x.id.length > 0);

      return {
        post_id: postId,
        thread_id: String(row.thread_id ?? '').trim(),
        thread_title: String(row.thread_title ?? '').trim(),
        audience_mode: parseAudienceMode(row.audience_mode),
        role_names: Array.isArray(row.role_names)
          ? row.role_names.map((x) => String(x)).filter(Boolean)
          : [],
        ressort_names: Array.isArray(row.ressort_names)
          ? row.ressort_names.map((x) => String(x)).filter(Boolean)
          : [],
        user_id: String(row.user_id ?? '').trim(),
        author_name: String(row.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
        body: row.body == null ? null : String(row.body),
        external_url: row.external_url == null ? null : String(row.external_url),
        approval_status: parseApprovalStatus(row.approval_status),
        approval_request_note:
          row.approval_request_note == null ? null : String(row.approval_request_note),
        approval_comment: row.approval_comment == null ? null : String(row.approval_comment),
        approval_requested_at:
          row.approval_requested_at == null ? null : String(row.approval_requested_at),
        approval_decided_at:
          row.approval_decided_at == null ? null : String(row.approval_decided_at),
        created_at: String(row.created_at ?? '').trim(),
        attachments,
      };
    })
    .filter((x): x is CostumeRequestRow => !!x);
}

function parseMediaRows(raw: unknown): MediaRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      const id = String(row.attachment_id ?? '').trim();
      if (!id) return null;
      return {
        attachment_id: id,
        type: String(row.type ?? 'file').toLowerCase(),
        bucket_id: row.bucket_id == null ? null : String(row.bucket_id),
        object_path: row.object_path == null ? null : String(row.object_path),
        link_url: row.link_url == null ? null : String(row.link_url),
        file_name: row.file_name == null ? null : String(row.file_name),
        created_at: String(row.created_at ?? '').trim(),
        author_name: String(row.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
      };
    })
    .filter((row): row is MediaRow => !!row);
}

function parsePollRows(raw: unknown): PollRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      const pollId = String(row.poll_id ?? '').trim();
      if (!pollId) return null;

      const optionsRaw = Array.isArray(row.options) ? row.options : [];
      const options: PollOption[] = optionsRaw
        .map((entryOpt) => {
          if (!entryOpt || typeof entryOpt !== 'object') return null;
          const map = entryOpt as Record<string, unknown>;
          const id = String(map.id ?? '').trim();
          if (!id) return null;
          const votesRaw = Array.isArray(map.votes) ? map.votes : [];
          const votes: PollVoteDetail[] = votesRaw
            .map((entryVote) => {
              if (!entryVote || typeof entryVote !== 'object') return null;
              const voteMap = entryVote as Record<string, unknown>;
              const userId = String(voteMap.user_id ?? '').trim();
              if (!userId) return null;
              return {
                user_id: userId,
                author_name: String(voteMap.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
                vote_note: voteMap.vote_note == null ? null : String(voteMap.vote_note),
              };
            })
            .filter((x): x is PollVoteDetail => !!x);
          return {
            id,
            option_text: String(map.option_text ?? '').trim(),
            sort_order: Number(map.sort_order ?? 0) || 0,
            vote_count: Number(map.vote_count ?? 0) || 0,
            selected_by_me: map.selected_by_me === true,
            votes,
          };
        })
        .filter((x): x is PollOption => !!x);

      return {
        poll_id: pollId,
        thread_id: String(row.thread_id ?? '').trim(),
        question: String(row.question ?? '').trim(),
        allow_multiple: row.allow_multiple === true,
        is_closed: row.is_closed === true,
        closes_at: row.closes_at == null ? null : String(row.closes_at),
        created_at: String(row.created_at ?? '').trim(),
        created_by: String(row.created_by ?? '').trim(),
        author_name: String(row.author_name ?? 'Unbekannt').trim() || 'Unbekannt',
        can_manage: row.can_manage === true,
        voter_count: Number(row.voter_count ?? 0) || 0,
        collect_vote_note: row.collect_vote_note === true,
        vote_note_label: String(row.vote_note_label ?? 'Groesse / Hinweis').trim() || 'Groesse / Hinweis',
        my_option_ids: Array.isArray(row.my_option_ids)
          ? row.my_option_ids.map((x) => String(x)).filter(Boolean)
          : [],
        my_vote_note: row.my_vote_note == null ? null : String(row.my_vote_note),
        options,
      };
    })
    .filter((x): x is PollRow => !!x);
}

function parseProfilePayload(raw: unknown): ProfilePayload {
  const payload = raw && typeof raw === 'object'
    ? (raw as Record<string, unknown>)
    : {};
  const fieldsRaw = Array.isArray(payload.fields) ? payload.fields : [];
  const fields: FieldRow[] = [];
  for (const entry of fieldsRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const id = String(row.id ?? '').trim();
    if (!id) continue;
    const options = Array.isArray(row.options)
      ? row.options.map((x) => String(x)).filter((x) => x.trim().length > 0)
      : [];
    fields.push({
      id,
      field_key: String(row.field_key ?? '').trim(),
      label: String(row.label ?? '').trim(),
      field_type: String(row.field_type ?? 'text').trim(),
      options,
      is_required: row.is_required === true,
      sort_order: Number(row.sort_order ?? 0) || 0,
      role_ids: Array.isArray(row.role_ids)
        ? row.role_ids.map((x) => String(x)).filter(Boolean)
        : [],
      role_names: Array.isArray(row.role_names)
        ? row.role_names.map((x) => String(x)).filter(Boolean)
        : [],
      value: row.value == null ? null : String(row.value),
    });
  }
  fields.sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
  });

  return {
    can_manage: payload.can_manage === true,
    target_user_id: payload.target_user_id == null ? null : String(payload.target_user_id),
    target_user_name: payload.target_user_name == null ? null : String(payload.target_user_name),
    fields,
  };
}

function parseProfileUsers(raw: unknown): ProfileUserRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      const userId = String(row.user_id ?? '').trim();
      if (!userId) return null;
      return {
        user_id: userId,
        display_name: String(row.display_name ?? 'Unbekannt').trim() || 'Unbekannt',
        role_ids: Array.isArray(row.role_ids)
          ? row.role_ids.map((x) => String(x)).filter(Boolean)
          : [],
        role_names: Array.isArray(row.role_names)
          ? row.role_names.map((x) => String(x)).filter(Boolean)
          : [],
      };
    })
    .filter((row): row is ProfileUserRow => !!row);
}

function parseRolePageRows(raw: unknown): RolePageRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): RolePageRow | null => {
      const row = entry as Record<string, unknown>;
      const id = String(row.id ?? '').trim();
      if (!id) return null;
      return {
        id,
        title: String(row.title ?? '').trim(),
        body: String(row.body ?? ''),
        sort_order: Number(row.sort_order ?? 0) || 0,
        is_pinned: row.is_pinned === true,
        is_published: row.is_published !== false,
        created_at: String(row.created_at ?? '').trim(),
        updated_at: String(row.updated_at ?? '').trim(),
        role_ids: Array.isArray(row.role_ids)
          ? row.role_ids.map((x) => String(x)).filter(Boolean)
          : [],
        role_names: Array.isArray(row.role_names)
          ? row.role_names.map((x) => String(x)).filter(Boolean)
          : [],
        can_manage: row.can_manage === true,
        primary_image_url: (String(row.primary_image_url ?? '').trim().length > 0) ? String(row.primary_image_url).trim() : null,
      };
    })
    .filter((row): row is RolePageRow => row !== null);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mediaKindForFile(file: File): 'image' | 'pdf' | 'file' {
  const mime = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  if (
    mime.startsWith('image/') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.webp')
  ) {
    return 'image';
  }
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  return 'file';
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_.-]/g, '_').replace(/\.\.+/g, '.');
}

async function openAttachment(att: {
  link_url?: string | null;
  bucket_id?: string | null;
  object_path?: string | null;
}) {
  const direct = (att.link_url ?? '').trim();
  if (direct) {
    window.open(direct, '_blank', 'noopener,noreferrer');
    return;
  }

  const bucket = (att.bucket_id ?? '').trim();
  const objectPath = (att.object_path ?? '').trim();
  if (!bucket || !objectPath) {
    throw new Error('Attachment path missing');
  }

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(objectPath, 60 * 10);
  if (error) throw error;
  const url = data?.signedUrl;
  if (!url) throw new Error('No signed url');
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function CostumesPage() {
  const qc = useQueryClient();

  useEffect(() => {
    document.title = 'Kostüme – Musical!Kultur Admin';
  }, []);

  const [threadTitle, setThreadTitle] = useState('');
  const [threadDescription, setThreadDescription] = useState('');
  const [threadAudienceMode, setThreadAudienceMode] = useState<'all' | 'targeted'>('targeted');
  const [threadRoleIds, setThreadRoleIds] = useState<string[]>([]);
  const [threadRessortIds, setThreadRessortIds] = useState<string[]>([]);
  const [threadContextType, setThreadContextType] = useState<'general' | 'scene' | 'song' | 'ensemble'>('general');
  const [threadContextLabel, setThreadContextLabel] = useState('');
  const [threadSceneId, setThreadSceneId] = useState<string | null>(null);

  const [selectedThreadId, setSelectedThreadId] = useState<string>('');

  const [postBody, setPostBody] = useState('');
  const [postLink, setPostLink] = useState('');
  const [postPinned, setPostPinned] = useState(false);
  const [postRequestApproval, setPostRequestApproval] = useState(false);
  const [postApprovalNote, setPostApprovalNote] = useState('');
  const [postFiles, setPostFiles] = useState<File[]>([]);
  const [postUploadStatus, setPostUploadStatus] = useState<Record<string, { state: 'queued' | 'uploading' | 'done' | 'failed'; message?: string }>>({});

  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [reviewCommentByPostId, setReviewCommentByPostId] = useState<Record<string, string>>({});
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptionsText, setPollOptionsText] = useState('');
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const [pollCollectVoteNote, setPollCollectVoteNote] = useState(false);
  const [pollVoteNoteLabel, setPollVoteNoteLabel] = useState('Groesse / Hinweis');
  const [pollSelections, setPollSelections] = useState<Record<string, string[]>>({});
  const [pollVoteNotes, setPollVoteNotes] = useState<Record<string, string>>({});

  const [fieldId, setFieldId] = useState<string>('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<'text' | 'number' | 'select' | 'multiline'>('text');
  const [fieldOptions, setFieldOptions] = useState('');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldSort, setFieldSort] = useState('0');
  const [fieldRoleIds, setFieldRoleIds] = useState<string[]>([]);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string>('');
  const [managedProfileValues, setManagedProfileValues] = useState<Record<string, string>>({});
  const [profileRoleFilter, setProfileRoleFilter] = useState<string>('');
  const [overviewRoleFilter, setOverviewRoleFilter] = useState<string>('');
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  const [rolePageId, setRolePageId] = useState<string>('');
  const [rolePageTitle, setRolePageTitle] = useState('');
  const [rolePageBody, setRolePageBody] = useState('');
  const [rolePageRoleIds, setRolePageRoleIds] = useState<string[]>([]);
  const [rolePageSort, setRolePageSort] = useState('0');
  const [rolePagePinned, setRolePagePinned] = useState(false);
  const [rolePagePublished, setRolePagePublished] = useState(true);
  const [rolePageImageUrl, setRolePageImageUrl] = useState<string | null>(null);
  const [rolePageImageUploading, setRolePageImageUploading] = useState(false);

  const rolesQ = useQuery({
    queryKey: ['costume', 'roles'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RoleRow[]> => {
      const { data, error } = await supabase
        .from('roles')
        .select('id,name')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as RoleRow[];
    },
  });

  const ressortsQ = useQuery({
    queryKey: ['costume', 'ressorts'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RessortRow[]> => {
      const { data, error } = await supabase
        .from('ressorts')
        .select('id,name')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as RessortRow[];
    },
  });

  const scenesQ = useQuery({
    queryKey: ['costume', 'scenes'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<SceneRow[]> => {
      const { data, error } = await supabase.rpc('get_scenes_list');
      if (error) throw error;
      return parseSceneRows(data);
    },
  });

  const threadsQ = useQuery({
    queryKey: ['costume', 'threads'],
    staleTime: 60_000,
    queryFn: async (): Promise<ThreadRow[]> => {
      const { data, error } = await supabase.rpc('get_my_costume_threads');
      if (error) throw error;
      return parseThreadRows(data);
    },
  });

  const profileQ = useQuery({
    queryKey: ['costume', 'profile'],
    staleTime: 60_000,
    queryFn: async (): Promise<ProfilePayload> => {
      const { data, error } = await supabase.rpc('get_my_costume_profile');
      if (error) throw error;
      return parseProfilePayload(data);
    },
  });

  const rolePagesQ = useQuery({
    queryKey: ['costume', 'role-pages'],
    staleTime: 60_000,
    queryFn: async (): Promise<RolePageRow[]> => {
      const { data, error } = await supabase.rpc('get_my_costume_role_pages');
      if (error) throw error;
      return parseRolePageRows(data);
    },
  });

  const profileUsersQ = useQuery({
    queryKey: ['costume', 'profile-users'],
    enabled: profileQ.data?.can_manage === true,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<ProfileUserRow[]> => {
      const { data, error } = await supabase.rpc('get_costume_profile_users');
      if (error) throw error;
      return parseProfileUsers(data);
    },
  });

  const managedProfileQ = useQuery({
    queryKey: ['costume', 'profile-for-user', selectedProfileUserId || 'none'],
    enabled: profileQ.data?.can_manage === true && selectedProfileUserId.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<ProfilePayload> => {
      const { data, error } = await supabase.rpc('get_costume_profile_for_user', {
        p_user_id: selectedProfileUserId,
      });
      if (error) throw error;
      return parseProfilePayload(data);
    },
  });

  const overviewQ = useQuery({
    queryKey: ['costume', 'profile-overview'],
    queryFn: async (): Promise<OverviewPayload> => {
      const { data, error } = await supabase.rpc('get_costume_profile_overview');
      if (error) throw error;
      const d = (data ?? {}) as { fields?: unknown[]; users?: unknown[] };
      const fields: OverviewField[] = (d.fields ?? []).map((entry) => {
        const f = (entry ?? {}) as Record<string, unknown>;
        return {
          id: String(f.id ?? ''),
          field_key: String(f.field_key ?? ''),
          label: String(f.label ?? ''),
          field_type: String(f.field_type ?? 'text'),
          is_required: Boolean(f.is_required),
          sort_order: Number(f.sort_order ?? 0),
          role_ids: Array.isArray(f.role_ids) ? f.role_ids.map(String) : [],
          role_names: Array.isArray(f.role_names) ? f.role_names.map(String) : [],
        };
      });
      const users: OverviewUser[] = (d.users ?? []).map((entry) => {
        const u = (entry ?? {}) as Record<string, unknown>;
        return {
          user_id: String(u.user_id ?? ''),
          display_name: String(u.display_name ?? 'Unbekannt'),
          role_ids: Array.isArray(u.role_ids) ? u.role_ids.map(String) : [],
          role_names: Array.isArray(u.role_names) ? u.role_names.map(String) : [],
          values:
            typeof u.values === 'object' && u.values !== null
              ? Object.fromEntries(
                  Object.entries(u.values).map(([k, v]) => [k, v == null ? null : String(v)]),
                )
              : {},
        };
      });
      return { fields, users };
    },
    enabled: profileQ.data?.can_manage === true,
    staleTime: 60_000,
  });

  const selectedThread = useMemo(
    () => (threadsQ.data ?? []).find((thread) => thread.id === selectedThreadId) ?? null,
    [selectedThreadId, threadsQ.data],
  );

  const postsQ = useQuery({
    queryKey: ['costume', 'posts', selectedThreadId || 'none'],
    enabled: selectedThreadId.length > 0 && selectedThread != null,
    staleTime: 30_000,
    queryFn: async (): Promise<PostRow[]> => {
      const { data, error } = await supabase.rpc('get_costume_thread_posts', {
        p_thread_id: selectedThreadId,
      });
      if (error) throw error;
      return parsePostRows(data);
    },
  });

  const mediaQ = useQuery({
    queryKey: ['costume', 'media', selectedThreadId || 'none'],
    enabled: selectedThreadId.length > 0 && selectedThread != null,
    staleTime: 60_000,
    queryFn: async (): Promise<MediaRow[]> => {
      const { data, error } = await supabase.rpc('get_costume_thread_media', {
        p_thread_id: selectedThreadId,
      });
      if (error) throw error;
      return parseMediaRows(data);
    },
  });

  const requestsQ = useQuery({
    queryKey: ['costume', 'requests', 'pending'],
    enabled: profileQ.data?.can_manage === true,
    staleTime: 30_000,
    queryFn: async (): Promise<CostumeRequestRow[]> => {
      const { data, error } = await supabase.rpc('get_costume_post_requests', {
        p_thread_id: null,
        p_status: 'pending',
      });
      if (error) throw error;
      return parseRequestRows(data);
    },
  });

  const pollsQ = useQuery({
    queryKey: ['costume', 'polls', selectedThreadId || 'none'],
    enabled: selectedThreadId.length > 0 && selectedThread != null,
    staleTime: 60_000,
    queryFn: async (): Promise<PollRow[]> => {
      const { data, error } = await supabase.rpc('get_costume_thread_polls', {
        p_thread_id: selectedThreadId,
      });
      if (error) throw error;
      return parsePollRows(data);
    },
  });

  useEffect(() => {
    const threads = threadsQ.data ?? [];
    if (threads.length === 0) {
      if (!selectedThreadId) return;
      setSelectedThreadId('');
      return;
    }

    if (!selectedThreadId || !threads.some((thread) => thread.id === selectedThreadId)) {
      setSelectedThreadId(threads[0].id);
    }
  }, [selectedThreadId, threadsQ.data]);

  useEffect(() => {
    const nextSelections: Record<string, string[]> = {};
    const nextNotes: Record<string, string> = {};
    for (const poll of pollsQ.data ?? []) {
      nextSelections[poll.poll_id] = [...poll.my_option_ids];
      nextNotes[poll.poll_id] = poll.my_vote_note ?? '';
    }
    setPollSelections(nextSelections);
    setPollVoteNotes(nextNotes);
  }, [pollsQ.data]);

  useEffect(() => {
    if (selectedProfileUserId) return;
    const first = (profileUsersQ.data ?? [])[0];
    if (!first) return;
    setSelectedProfileUserId(first.user_id);
  }, [profileUsersQ.data, selectedProfileUserId]);

  useEffect(() => {
    const fields = managedProfileQ.data?.fields ?? [];
    if (fields.length === 0) {
      setManagedProfileValues({});
      return;
    }
    const next: Record<string, string> = {};
    for (const field of fields) {
      next[field.id] = field.value ?? '';
    }
    setManagedProfileValues(next);
  }, [managedProfileQ.data]);

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (refreshTimer !== null) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void qc.invalidateQueries({ queryKey: ['costume'] });
      }, 180);
    };

    const ch = supabase
      .channel('admin-costume-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_threads' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_thread_roles' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_thread_ressorts' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_posts' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_post_comments' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_post_attachments' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_roles' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_ressorts' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_profile_fields' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_profile_values' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_role_pages' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_role_page_roles' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_polls' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_poll_options' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'costume_poll_votes' }, scheduleRefresh)
      .subscribe();

    return () => {
      if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
      }
      void supabase.removeChannel(ch);
    };
  }, [qc]);

  const createThreadM = useMutation({
    mutationFn: async () => {
      const title = threadTitle.trim();
      const description = threadDescription.trim();
      if (!title) throw new Error('Titel fehlt');
      if (threadAudienceMode === 'targeted' && threadRoleIds.length === 0 && threadRessortIds.length === 0) {
        throw new Error('Bitte mindestens eine Rolle oder Abteilung auswählen.');
      }
      const contextLabel = threadContextLabel.trim() || null;

      const { error } = await supabase.rpc('create_costume_thread', {
        p_title: title,
        p_description: description || null,
        p_role_ids: threadAudienceMode === 'all' ? null : (threadRoleIds.length > 0 ? threadRoleIds : null),
        p_context_type: threadContextType,
        p_context_label: threadContextType === 'song' ? contextLabel : null,
        p_scene_id: threadContextType === 'scene' ? threadSceneId : null,
        p_ressort_ids: threadAudienceMode === 'all' ? null : (threadRessortIds.length > 0 ? threadRessortIds : null),
        p_audience_mode: threadAudienceMode,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setThreadTitle('');
      setThreadDescription('');
      setThreadAudienceMode('targeted');
      setThreadRoleIds([]);
      setThreadRessortIds([]);
      setThreadContextType('general');
      setThreadContextLabel('');
      setThreadSceneId(null);
      notifications.show({ color: 'teal', title: 'Erstellt', message: 'Kostüm-Thread erstellt.' });
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: (error as Error).message || 'Erstellen fehlgeschlagen.' });
    },
  });

  const createPostM = useMutation({
    mutationFn: async () => {
      if (!selectedThreadId) throw new Error('Kein Thread gewählt');
      const body = postBody.trim();
      const link = postLink.trim();
      const approvalNote = postApprovalNote.trim();
      if (!body && !link && postFiles.length === 0) {
        throw new Error('Bitte Text, Link oder Datei angeben.');
      }
      if (link && !(link.startsWith('http://') || link.startsWith('https://'))) {
        throw new Error('Link muss mit http:// oder https:// starten.');
      }
      if (postRequestApproval && postPinned) {
        throw new Error('Posts mit Genehmigungsanfrage können nicht direkt angepinnt werden.');
      }

      const { data: postIdRaw, error: postError } = await supabase.rpc('create_costume_post', {
        p_thread_id: selectedThreadId,
        p_body: body || null,
        p_external_url: link || null,
        p_is_pinned: postPinned,
        p_request_approval: postRequestApproval,
        p_approval_request_note: approvalNote || null,
        p_has_attachments: postFiles.length > 0,
      });
      if (postError) throw postError;
      const postId = String(postIdRaw ?? '').trim();
      if (!postId) throw new Error('Post konnte nicht erstellt werden.');

      for (const file of postFiles) {
        setPostUploadStatus((prev) => ({
          ...prev,
          [uploadKeyForFile(file)]: { state: 'uploading' },
        }));
        const safeName = sanitizeFileName(file.name);
        const objectPath = `thread/${selectedThreadId}/${postId}/${Date.now()}_${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from('costume-files')
          .upload(objectPath, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type || undefined,
          });
        if (uploadError) throw uploadError;

        const type = mediaKindForFile(file);
        const { error: insertError } = await supabase.from('costume_post_attachments').insert({
          post_id: postId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          type,
          bucket_id: 'costume-files',
          object_path: objectPath,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
        });
        if (insertError) throw insertError;
        setPostUploadStatus((prev) => ({
          ...prev,
          [uploadKeyForFile(file)]: { state: 'done' },
        }));
      }
    },
    onSuccess: async () => {
      setPostBody('');
      setPostLink('');
      setPostFiles([]);
      setPostUploadStatus({});
      setPostPinned(false);
      setPostRequestApproval(false);
      setPostApprovalNote('');
      notifications.show({
        color: 'teal',
        title: 'Gesendet',
        message: postRequestApproval
          ? 'Entscheidung zur Genehmigung eingereicht.'
          : 'Kostüm-Post veröffentlicht.',
      });
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'posts'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'media'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'requests'] });
    },
    onError: (error) => {
      const file = postFiles.find((entry) => postUploadStatus[uploadKeyForFile(entry)]?.state === 'uploading');
      if (file) {
        setPostUploadStatus((prev) => ({
          ...prev,
          [uploadKeyForFile(file)]: {
            state: 'failed',
            message: (error as Error).message || 'Upload fehlgeschlagen.',
          },
        }));
      }
      notifications.show({ color: 'red', title: 'Fehler', message: (error as Error).message || 'Post fehlgeschlagen.' });
    },
  });

  const togglePinM = useMutation({
    mutationFn: async (post: PostRow) => {
      const { error } = await supabase.rpc('set_costume_post_pinned', {
        p_post_id: post.id,
        p_is_pinned: !post.is_pinned,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'posts'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Pin fehlgeschlagen', message: (error as Error).message || 'Pin konnte nicht geändert werden.' });
    },
  });

  const toggleReactionM = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase.rpc('toggle_costume_post_reaction', {
        p_post_id: postId,
        p_reaction: 'like',
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['costume', 'posts'] });
    },
  });

  const addCommentM = useMutation({
    mutationFn: async (postId: string) => {
      const text = (commentDrafts[postId] ?? '').trim();
      if (!text) throw new Error('Kommentar ist leer');
      const { error } = await supabase.rpc('add_costume_post_comment', {
        p_post_id: postId,
        p_comment_text: text,
      });
      if (error) throw error;
    },
    onSuccess: async (_data, postId) => {
      setCommentDrafts((prev) => ({ ...prev, [postId]: '' }));
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'posts'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Kommentar fehlgeschlagen', message: (error as Error).message || 'Kommentar konnte nicht gesendet werden.' });
    },
  });

  const reviewRequestM = useMutation({
    mutationFn: async (params: { postId: string; action: 'approve' | 'reject' }) => {
      const comment = (reviewCommentByPostId[params.postId] ?? '').trim();
      const { error } = await supabase.rpc('review_costume_post', {
        p_post_id: params.postId,
        p_action: params.action,
        p_comment: comment || null,
      });
      if (error) throw error;
    },
    onSuccess: async (_data, vars) => {
      setReviewCommentByPostId((prev) => ({ ...prev, [vars.postId]: '' }));
      notifications.show({
        color: 'teal',
        title: 'Anfrage bearbeitet',
        message: vars.action === 'approve' ? 'Anfrage genehmigt.' : 'Anfrage abgelehnt.',
      });
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'posts'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'requests'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Genehmigung fehlgeschlagen',
        message: (error as Error).message || 'Anfrage konnte nicht bearbeitet werden.',
      });
    },
  });

  const createPollM = useMutation({
    mutationFn: async () => {
      if (!selectedThreadId) throw new Error('Kein Thread gewählt');
      const question = pollQuestion.trim();
      const voteNoteLabel = pollVoteNoteLabel.trim();
      const options = pollOptionsText
        .split(/\n|,/g)
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
      if (!question) throw new Error('Frage fehlt');
      if (options.length < 2) throw new Error('Mindestens 2 Antwortoptionen nötig');
      if (pollCollectVoteNote && !voteNoteLabel) {
        throw new Error('Bitte Feldname fuer Bestellinfos angeben.');
      }

      const { error } = await supabase.rpc('create_costume_poll', {
        p_thread_id: selectedThreadId,
        p_question: question,
        p_options: options,
        p_allow_multiple: pollAllowMultiple,
        p_collect_vote_note: pollCollectVoteNote,
        p_vote_note_label: pollCollectVoteNote ? voteNoteLabel : null,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setPollQuestion('');
      setPollOptionsText('');
      setPollAllowMultiple(false);
      setPollCollectVoteNote(false);
      setPollVoteNoteLabel('Groesse / Hinweis');
      notifications.show({
        color: 'teal',
        title: 'Umfrage erstellt',
        message: 'Die Umfrage wurde in der Sammlung veröffentlicht.',
      });
      await qc.invalidateQueries({ queryKey: ['costume', 'polls'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'threads'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Poll fehlgeschlagen',
        message: (error as Error).message || 'Umfrage konnte nicht erstellt werden.',
      });
    },
  });

  const votePollM = useMutation({
    mutationFn: async (params: { pollId: string; optionIds: string[]; voteNote: string | null }) => {
      const { error } = await supabase.rpc('vote_costume_poll', {
        p_poll_id: params.pollId,
        p_option_ids: params.optionIds,
        p_vote_note: params.voteNote,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['costume', 'polls'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Abstimmung fehlgeschlagen',
        message: (error as Error).message || 'Stimme konnte nicht gespeichert werden.',
      });
    },
  });

  const closePollM = useMutation({
    mutationFn: async (params: { pollId: string; isClosed: boolean }) => {
      const { error } = await supabase.rpc('set_costume_poll_closed', {
        p_poll_id: params.pollId,
        p_is_closed: params.isClosed,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['costume', 'polls'] });
    },
  });

  const upsertFieldM = useMutation({
    mutationFn: async () => {
      const label = fieldLabel.trim();
      if (!label) throw new Error('Feldname fehlt');

      const options = fieldType === 'select'
        ? fieldOptions
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x.length > 0)
        : [];

      const sortOrder = Number(fieldSort);
      const { error } = await supabase.rpc('upsert_costume_profile_field', {
        p_field_id: fieldId || null,
        p_field_key: fieldKey.trim() || null,
        p_label: label,
        p_field_type: fieldType,
        p_options: options,
        p_is_required: fieldRequired,
        p_sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
        p_role_ids: fieldRoleIds.length > 0 ? fieldRoleIds : null,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Gespeichert', message: 'Profilfeld gespeichert.' });
      setFieldId('');
      setFieldKey('');
      setFieldLabel('');
      setFieldType('text');
      setFieldOptions('');
      setFieldRequired(false);
      setFieldSort('0');
      setFieldRoleIds([]);
      await qc.invalidateQueries({ queryKey: ['costume', 'profile'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Feld-Fehler', message: (error as Error).message || 'Feld konnte nicht gespeichert werden.' });
    },
  });

  const deleteFieldM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_costume_profile_field', {
        p_field_id: id,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Gelöscht', message: 'Profilfeld gelöscht.' });
      await qc.invalidateQueries({ queryKey: ['costume', 'profile'] });
    },
    onError: (error) => {
      notifications.show({ color: 'red', title: 'Löschen fehlgeschlagen', message: (error as Error).message || 'Feld konnte nicht gelöscht werden.' });
    },
  });

  const saveManagedProfileM = useMutation({
    mutationFn: async () => {
      if (!selectedProfileUserId) throw new Error('Bitte zuerst eine Person auswählen.');
      const fields = managedProfileQ.data?.fields ?? [];
      if (fields.length === 0) return;

      const payload: Record<string, string> = {};
      for (const field of fields) {
        payload[field.id] = (managedProfileValues[field.id] ?? '').trim();
      }

      const { error } = await supabase.rpc('upsert_costume_profile_values_for_user', {
        p_user_id: selectedProfileUserId,
        p_values: payload,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Gespeichert', message: 'ICH-Werte wurden aktualisiert.' });
      await qc.invalidateQueries({ queryKey: ['costume', 'profile-for-user'] });
      await qc.invalidateQueries({ queryKey: ['costume', 'profile'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'ICH-Speichern fehlgeschlagen',
        message: (error as Error).message || 'Werte konnten nicht gespeichert werden.',
      });
    },
  });

  const deleteThreadM = useMutation({
    mutationFn: async (thread: ThreadRow): Promise<{ storageWarnings: number }> => {
      // Fetch media to clean up storage first
      const { data: mediaRaw, error: mediaError } = await supabase.rpc('get_costume_thread_media', {
        p_thread_id: thread.id,
      });
      // Non-fatal: if media fetch fails (e.g. already deleted), skip storage cleanup
      const media = mediaError ? [] : parseMediaRows(mediaRaw);
      const objectsByBucket = new Map<string, string[]>();
      for (const item of media) {
        const bucket = (item.bucket_id ?? '').trim();
        const objectPath = (item.object_path ?? '').trim();
        if (!bucket || !objectPath) continue;
        const existing = objectsByBucket.get(bucket) ?? [];
        existing.push(objectPath);
        objectsByBucket.set(bucket, existing);
      }

      let storageWarnings = 0;
      for (const [bucket, paths] of objectsByBucket.entries()) {
        for (let i = 0; i < paths.length; i += 100) {
          const chunk = paths.slice(i, i + 100);
          const { error: removeError } = await supabase.storage.from(bucket).remove(chunk);
          if (removeError) storageWarnings += 1;
        }
      }

      const { error: deleteError } = await supabase.from('costume_threads').delete().eq('id', thread.id);
      if (deleteError) throw deleteError;
      return { storageWarnings };
    },
    onMutate: async (thread) => {
      setDeletingThreadId(thread.id);

      // Optimistic update: remove thread from cache immediately
      await qc.cancelQueries({ queryKey: ['costume', 'threads'] });
      const previousThreads = qc.getQueryData<ThreadRow[]>(['costume', 'threads']);
      qc.setQueryData<ThreadRow[]>(['costume', 'threads'], (old) =>
        (old ?? []).filter((t) => t.id !== thread.id),
      );

      // If this thread was selected, clear selection
      if (selectedThreadId === thread.id) {
        const remaining = (previousThreads ?? []).filter((t) => t.id !== thread.id);
        setSelectedThreadId(remaining[0]?.id ?? '');
      }

      return { previousThreads };
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['costume'] });
      notifications.show({
        color: result.storageWarnings > 0 ? 'yellow' : 'teal',
        title: result.storageWarnings > 0 ? 'Thread gelöscht (mit Warnung)' : 'Thread gelöscht',
        message: result.storageWarnings > 0
          ? 'Einige Dateipfade konnten nicht entfernt werden.'
          : 'Thread inkl. Inhalte wurde entfernt.',
      });
    },
    onError: (error, _thread, context) => {
      // Roll back optimistic update on error
      if (context?.previousThreads) {
        qc.setQueryData(['costume', 'threads'], context.previousThreads);
      }
      notifications.show({
        color: 'red',
        title: 'Thread-Löschen fehlgeschlagen',
        message: (error as Error).message || 'Thread konnte nicht gelöscht werden.',
      });
    },
    onSettled: () => {
      setDeletingThreadId(null);
    },
  });

  const resetRolePageForm = () => {
    setRolePageId('');
    setRolePageTitle('');
    setRolePageBody('');
    setRolePageRoleIds([]);
    setRolePageSort('0');
    setRolePagePinned(false);
    setRolePagePublished(true);
    setRolePageImageUrl(null);
  };

  const upsertRolePageM = useMutation({
    mutationFn: async () => {
      const title = rolePageTitle.trim();
      const body = rolePageBody.trim();
      if (!title) throw new Error('Titel fehlt');
      if (!body) throw new Error('Inhalt fehlt');

      const sortOrder = Number(rolePageSort);
      const { data, error } = await supabase.rpc('upsert_costume_role_page', {
        p_page_id: rolePageId || null,
        p_title: title,
        p_body: body,
        p_sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
        p_is_pinned: rolePagePinned,
        p_is_published: rolePagePublished,
        p_role_ids: rolePageRoleIds.length > 0 ? rolePageRoleIds : null,
      });
      if (error) throw error;
      
      // Get the actual page ID (either from existing or newly created)
      const actualPageId = rolePageId || String(data ?? '').trim();
      if (!actualPageId) throw new Error('Seite konnte nicht gespeichert werden');
      
      // Update image if provided
      if (rolePageImageUrl) {
        const { error: updateErr } = await supabase
          .from('costume_role_pages')
          .update({ primary_image_url: rolePageImageUrl })
          .eq('id', actualPageId);
        if (updateErr) throw updateErr;
      }
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Gespeichert',
        message: rolePageId ? 'Info-Seite aktualisiert.' : 'Info-Seite angelegt.',
      });
      resetRolePageForm();
      await qc.invalidateQueries({ queryKey: ['costume', 'role-pages'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Info-Seite fehlgeschlagen',
        message: (error as Error).message || 'Seite konnte nicht gespeichert werden.',
      });
    },
  });

  const deleteRolePageM = useMutation({
    mutationFn: async (pageId: string) => {
      const { error } = await supabase.rpc('delete_costume_role_page', {
        p_page_id: pageId,
      });
      if (error) throw error;
    },
    onSuccess: async (_data, pageId) => {
      if (rolePageId === pageId) resetRolePageForm();
      notifications.show({ color: 'teal', title: 'Gelöscht', message: 'Info-Seite entfernt.' });
      await qc.invalidateQueries({ queryKey: ['costume', 'role-pages'] });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Löschen fehlgeschlagen',
        message: (error as Error).message || 'Info-Seite konnte nicht gelöscht werden.',
      });
    },
  });

  const roleOptions = useMemo(
    () =>
      (rolesQ.data ?? []).map((role) => ({
        value: role.id,
        label: role.name,
      })),
    [rolesQ.data],
  );

  const ressortOptions = useMemo(
    () =>
      (ressortsQ.data ?? []).map((ressort) => ({
        value: ressort.id,
        label: ressort.name,
      })),
    [ressortsQ.data],
  );

  const uploadKeyForFile = (file: File) => `${file.name}|${file.size}|${file.type}`;
  const uploadLabelForState = (state: 'queued' | 'uploading' | 'done' | 'failed') => {
    if (state === 'uploading') return 'Wird hochgeladen';
    if (state === 'done') return 'Hochgeladen';
    if (state === 'failed') return 'Fehlgeschlagen';
    return 'Bereit';
  };
  const uploadColorForState = (state: 'queued' | 'uploading' | 'done' | 'failed') => {
    if (state === 'uploading') return 'blue';
    if (state === 'done') return 'teal';
    if (state === 'failed') return 'red';
    return 'gray';
  };
  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  };
  const mergePickedFiles = (incoming: File[]) => {
    const accepted: File[] = [];
    for (const file of incoming) {
      if (file.size > 209715200) {
        notifications.show({
          color: 'yellow',
          title: 'Datei zu groß',
          message: `${file.name} ist größer als 200 MB.`,
        });
        continue;
      }
      accepted.push(file);
    }
    setPostFiles((prev) => {
      const merged = [...prev];
      for (const file of accepted) {
        const duplicate = merged.some(
          (existing) => existing.name === file.name && existing.size === file.size,
        );
        if (!duplicate) merged.push(file);
      }
      return merged;
    });
    setPostUploadStatus((prev) => {
      const next = { ...prev };
      for (const file of accepted) {
        next[uploadKeyForFile(file)] = { state: 'queued' };
      }
      return next;
    });
  };

  const applyEnsembleThreadTemplate = () => {
    if (!threadTitle.trim()) {
      setThreadTitle('Ensemble Kostüme');
    }
    setThreadAudienceMode('targeted');
    setThreadContextType('ensemble');
    setThreadContextLabel('');
    setThreadSceneId(null);
    setThreadRessortIds([]);
    const ensembleRoleIds = (rolesQ.data ?? [])
      .filter((role) => role.name.toLowerCase().includes('ensemble'))
      .map((role) => role.id);
    setThreadRoleIds(ensembleRoleIds);
  };

  const canManage = profileQ.data?.can_manage === true;
  const profileUserOptions = useMemo(
    () =>
      (profileUsersQ.data ?? [])
        .filter((entry) => !profileRoleFilter || entry.role_ids.includes(profileRoleFilter))
        .map((entry) => ({
          value: entry.user_id,
          label: entry.display_name,
        })),
    [profileUsersQ.data, profileRoleFilter],
  );
  const selectedProfileUser = useMemo(
    () => (profileUsersQ.data ?? []).find((entry) => entry.user_id === selectedProfileUserId) ?? null,
    [profileUsersQ.data, selectedProfileUserId],
  );

  const overviewAllRoles = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of overviewQ.data?.users ?? []) {
      u.role_ids.forEach((id, i) => {
        if (u.role_names[i]) map.set(id, u.role_names[i]!);
      });
    }
    return map;
  }, [overviewQ.data]);

  const filteredOverviewUsers = useMemo(() => {
    const users = overviewQ.data?.users ?? [];
    if (!overviewRoleFilter) return users;
    return users.filter((u) => u.role_ids.includes(overviewRoleFilter));
  }, [overviewQ.data, overviewRoleFilter]);

  const isDecisionRequestPost = (post: PostRow) => (
    !!post.approval_requested_at
    || !!(post.approval_request_note && post.approval_request_note.trim())
  );

  const shouldShowDecisionStatusBadge = (post: PostRow) => (
    isDecisionRequestPost(post)
    && (post.approval_status === 'pending' || post.approval_status === 'rejected')
  );

  const postStatusColor = (post: PostRow) => {
    if (!shouldShowDecisionStatusBadge(post)) return 'gray';
    if (post.approval_status === 'rejected') return 'red';
    return 'yellow';
  };

  const postStatusLabel = (post: PostRow) => {
    if (!shouldShowDecisionStatusBadge(post)) return '';
    if (post.approval_status === 'rejected') return 'Abgelehnt';
    return 'Entscheidung offen';
  };

  const togglePollSelection = (poll: PollRow, optionId: string) => {
    setPollSelections((prev) => {
      const current = prev[poll.poll_id] ?? [];
      if (poll.allow_multiple) {
        if (current.includes(optionId)) {
          return { ...prev, [poll.poll_id]: current.filter((id) => id !== optionId) };
        }
        return { ...prev, [poll.poll_id]: [...current, optionId] };
      }
      return { ...prev, [poll.poll_id]: [optionId] };
    });
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Kostüme"
        description="Rollenbasierter Austausch, Medien und persönliche Kostümfelder"
        actions={(
          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={async () => {
                await qc.invalidateQueries({ queryKey: ['costume'] });
              }}
            >
              Aktualisieren
            </Button>
          </Group>
        )}
      />

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder radius="md" p="md" className="contrast-surface-soft">
          <Stack gap="sm">
            <Text fw={700}>Sammlung anlegen</Text>
            <Text size="sm" c="dimmed">
              3 Schritte: Name → Rollen wählen → Erstellen. Danach können alle Betroffenen
              dort Infos, Fotos und Dateien teilen.
            </Text>
            {canManage && (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={applyEnsembleThreadTemplate}
                >
                  Vorlage: Ensemble Kostüme
                </Button>
              </Group>
            )}
            <Text size="sm" fw={600}>1 · Name</Text>
            <TextInput
              label="Titel"
              placeholder="z. B. Ensemble Kostüme, Glinda, Akt 1 Szene 3"
              value={threadTitle}
              onChange={(e) => setThreadTitle(e.currentTarget.value)}
            />
            <Textarea
              label="Beschreibung (optional)"
              minRows={2}
              value={threadDescription}
              onChange={(e) => setThreadDescription(e.currentTarget.value)}
            />

            {canManage && (
              <Select
                label="Typ (optional)"
                description="Hilft beim Sortieren – z. B. Ensemble, Szene oder Song"
                data={[
                  { value: 'general', label: '📂 Allgemein' },
                  { value: 'scene', label: '🎬 Szene' },
                  { value: 'song', label: '🎵 Song' },
                  { value: 'ensemble', label: '👥 Ensemble' },
                ]}
                value={threadContextType}
                onChange={(val) => {
                  setThreadContextType((val ?? 'general') as typeof threadContextType);
                  setThreadContextLabel('');
                  setThreadSceneId(null);
                }}
              />
            )}

            {canManage && threadContextType === 'scene' && (
              <Select
                label="Szene (optional)"
                placeholder="Szene auswählen..."
                data={(scenesQ.data ?? []).map((s) => ({
                  value: s.id,
                  label: `Akt ${s.act} · Szene ${s.scene_number} – ${s.name}`,
                }))}
                value={threadSceneId}
                onChange={setThreadSceneId}
                searchable
                clearable
              />
            )}

            {canManage && threadContextType === 'song' && (
              <TextInput
                label="Song-Titel (optional)"
                value={threadContextLabel}
                onChange={(e) => setThreadContextLabel(e.currentTarget.value)}
                placeholder="z.B. Memory, Defying Gravity..."
              />
            )}

            <Text size="sm" fw={600}>2 · Wer soll die Sammlung sehen?</Text>
            <Select
              label="Zielgruppe"
              description="Explizit festlegen, ob die Sammlung für alle oder nur für bestimmte Gruppen sichtbar ist."
              data={[
                { value: 'targeted', label: 'Gezielte Gruppe' },
                { value: 'all', label: 'Alle Mitglieder' },
              ]}
              value={threadAudienceMode}
              onChange={(value) => setThreadAudienceMode(value === 'all' ? 'all' : 'targeted')}
            />
            <MultiSelect
              label="Rollen"
              description={
                threadAudienceMode === 'all'
                  ? 'Bei "Alle Mitglieder" werden Rollen ignoriert.'
                  : 'Mindestens eine Rolle oder Abteilung auswählen.'
              }
              data={roleOptions}
              value={threadRoleIds}
              onChange={setThreadRoleIds}
              placeholder="Rollen auswählen…"
              searchable
              clearable
              disabled={threadAudienceMode === 'all'}
            />
            <MultiSelect
              label="Abteilungen"
              description={
                threadAudienceMode === 'all'
                  ? 'Bei "Alle Mitglieder" werden Abteilungen ignoriert.'
                  : 'Ergänzt die Zielgruppe nach Abteilungen.'
              }
              data={ressortOptions}
              value={threadRessortIds}
              onChange={setThreadRessortIds}
              placeholder="Abteilungen auswählen…"
              searchable
              clearable
              disabled={threadAudienceMode === 'all'}
            />
            <Text size="xs" c="dimmed">
              {threadAudienceMode === 'all'
                ? 'Sichtbar für alle vorhandenen Mitgliederkonten.'
                : buildThreadAudienceSummary({
                    audience_mode: 'targeted',
                    role_names: roleOptions.filter((role) => threadRoleIds.includes(role.value)).map((role) => role.label),
                    ressort_names: ressortOptions.filter((ressort) => threadRessortIds.includes(ressort.value)).map((ressort) => ressort.label),
                  })}
            </Text>
            <Text size="sm" fw={600}>3 · Fertig</Text>
            <Button
              leftSection={<IconPlus size={16} />}
              loading={createThreadM.isPending}
              onClick={() => createThreadM.mutate()}
            >
              Sammlung jetzt anlegen
            </Button>
          </Stack>
        </Card>

        <Card withBorder radius="md" p="md" className="contrast-surface-soft">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>Threads</Text>
              <Badge variant="light">{(threadsQ.data ?? []).length}</Badge>
            </Group>
            <Divider />
            {threadsQ.isLoading ? (
              <Text c="dimmed">Lade Threads...</Text>
            ) : (threadsQ.data ?? []).length === 0 ? (
              <EmptyState
                icon={<IconFile size={40} />}
                title="Keine Threads"
                description="Lege die erste Kostüm-Sammlung an."
              />
            ) : (
              <ScrollArea h={360}>
                <Stack gap="xs">
                  {(threadsQ.data ?? []).map((thread) => {
                    const isActive = selectedThreadId === thread.id;
                    return (
                      <Card
                        key={thread.id}
                        withBorder
                        padding="sm"
                        radius="md"
                        className="contrast-surface"
                        style={{
                          cursor: 'pointer',
                          borderColor: isActive ? 'var(--mantine-color-brand-6)' : undefined,
                        }}
                        onClick={() => setSelectedThreadId(thread.id)}
                      >
                        <Stack gap={6}>
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Group gap={6} wrap="wrap">
                                <Text fw={700} style={{ wordBreak: 'break-word' }}>{thread.title}</Text>
                                {thread.context_type !== 'general' && (() => {
                                  const label = getThreadContextLabel(thread);
                                  const colorMap: Record<string, string> = {
                                    scene: 'blue',
                                    song: 'violet',
                                    ensemble: 'teal',
                                  };
                                  const iconMap: Record<string, string> = {
                                    scene: '🎬',
                                    song: '🎵',
                                    ensemble: '👥',
                                  };
                                  return (
                                    <Badge
                                      size="sm"
                                      variant="light"
                                      color={colorMap[thread.context_type] ?? 'gray'}
                                    >
                                      {iconMap[thread.context_type] ?? ''} {label}
                                    </Badge>
                                  );
                                })()}
                              </Group>
                            </Box>
                            <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                              <Badge variant="light" size="sm">💬 {thread.post_count}</Badge>
                              <Badge variant="outline" size="sm">📎 {thread.media_count}</Badge>
                              {canManage && (
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="subtle"
                                  leftSection={<IconTrash size={12} />}
                                  loading={deleteThreadM.isPending && deletingThreadId === thread.id}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!window.confirm(`Thread "${thread.title}" wirklich löschen?\n\nAlle Beiträge, Kommentare und Dateien werden entfernt. Dies kann nicht rückgängig gemacht werden.`)) return;
                                    deleteThreadM.mutate(thread);
                                  }}
                                >
                                  Löschen
                                </Button>
                              )}
                            </Group>
                          </Group>
                          {!!thread.description && (
                            <Text size="sm" c="dimmed" lineClamp={2}>{thread.description}</Text>
                          )}
                          <Group gap={6} wrap="wrap">
                            {buildThreadAudienceLabels(thread).slice(0, 4).map((item) => (
                              <Badge
                                key={`${item.tone}-${item.label}`}
                                variant={item.tone === 'all' ? 'light' : 'dot'}
                                size="sm"
                                color={item.tone === 'all' ? 'teal' : item.tone === 'role' ? 'indigo' : 'cyan'}
                              >
                                {item.label}
                              </Badge>
                            ))}
                            {buildThreadAudienceLabels(thread).length > 4 && (
                              <Badge variant="dot" size="sm" color="gray">+{buildThreadAudienceLabels(thread).length - 4}</Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">{buildThreadAudienceSummary(thread)}</Text>
                          {!!thread.pinned_post?.body && (
                            <Text size="xs" c="yellow">📌 {thread.pinned_post.body}</Text>
                          )}
                          {!!thread.latest_post?.body && (
                            <Text size="xs" c="dimmed" lineClamp={1}>↩ {thread.latest_post.body}</Text>
                          )}
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      {canManage && (
        <Card withBorder radius="md" p="md" className="contrast-surface-soft">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={700}>Genehmigungs-Anfragen</Text>
              <Badge color="yellow" variant="light">
                {(requestsQ.data ?? []).length}
              </Badge>
            </Group>
            <Divider />
            {requestsQ.isLoading ? (
              <Text c="dimmed">Lade Anfragen...</Text>
            ) : (requestsQ.data ?? []).length === 0 ? (
              <Text c="dimmed">Keine offenen Anfragen.</Text>
            ) : (
              <Stack gap="sm">
                {(requestsQ.data ?? []).map((request) => (
                  <Card key={request.post_id} withBorder radius="md" padding="sm" className="contrast-surface">
                    <Stack gap={8}>
                      <Group justify="space-between" align="flex-start">
                        <Box>
                          <Text fw={700}>{request.thread_title || 'Thread'}</Text>
                          <Text size="xs" c="dimmed">
                            {request.author_name} · {formatDate(request.approval_requested_at || request.created_at)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {buildThreadAudienceSummary({
                              audience_mode: request.audience_mode,
                              role_names: request.role_names,
                              ressort_names: request.ressort_names,
                            })}
                          </Text>
                        </Box>
                        <Badge color={request.approval_status === 'pending' ? 'yellow' : 'gray'} variant="light">
                          {request.approval_status === 'pending' ? 'Offen' : request.approval_status}
                        </Badge>
                      </Group>

                      {!!request.body && <Text size="sm">{request.body}</Text>}
                      {!!request.external_url && (
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={<IconLink size={14} />}
                          onClick={() => window.open(request.external_url ?? '', '_blank', 'noopener,noreferrer')}
                        >
                          {request.external_url}
                        </Button>
                      )}

                      {!!request.approval_request_note && (
                        <Text size="xs" c="dimmed">
                          Notiz: {request.approval_request_note}
                        </Text>
                      )}

                      {request.attachments.length > 0 && (
                        <Group gap={6}>
                          {request.attachments.map((att) => (
                            <Button
                              key={att.id}
                              size="xs"
                              variant="outline"
                              leftSection={<IconFile size={12} />}
                              onClick={async () => {
                                try {
                                  await openAttachment(att);
                                } catch {
                                  notifications.show({
                                    color: 'red',
                                    title: 'Fehler',
                                    message: 'Anhang konnte nicht geöffnet werden.',
                                  });
                                }
                              }}
                            >
                              {att.file_name || att.type.toUpperCase()}
                            </Button>
                          ))}
                        </Group>
                      )}

                      <TextInput
                        label="Kommentar für Entscheidung (optional)"
                        value={reviewCommentByPostId[request.post_id] ?? ''}
                        onChange={(e) => setReviewCommentByPostId((prev) => ({
                          ...prev,
                          [request.post_id]: e.currentTarget.value,
                        }))}
                      />

                      <Group>
                        <Button
                          color="teal"
                          leftSection={<IconCheck size={14} />}
                          loading={reviewRequestM.isPending}
                          onClick={() => reviewRequestM.mutate({ postId: request.post_id, action: 'approve' })}
                        >
                          Genehmigen
                        </Button>
                        <Button
                          color="red"
                          variant="light"
                          leftSection={<IconTrash size={14} />}
                          loading={reviewRequestM.isPending}
                          onClick={() => reviewRequestM.mutate({ postId: request.post_id, action: 'reject' })}
                        >
                          Ablehnen
                        </Button>
                        <Button
                          variant="default"
                          onClick={() => setSelectedThreadId(request.thread_id)}
                        >
                          Thread öffnen
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      )}

      <Card withBorder radius="md" p="md" className="contrast-surface-soft">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={700}>Rollen-Info-Seiten</Text>
            <Badge variant="light">{(rolePagesQ.data ?? []).length}</Badge>
          </Group>

          {canManage && (
            <Card withBorder radius="md" padding="sm" className="contrast-surface">
              <Stack gap="sm">
                <Text fw={600}>{rolePageId ? 'Info-Seite bearbeiten' : 'Neue Info-Seite'}</Text>
                <TextInput
                  label="Titel"
                  value={rolePageTitle}
                  onChange={(e) => setRolePageTitle(e.currentTarget.value)}
                  placeholder="z.B. Gruppe Handy - Kostüme"
                />
                <Textarea
                  label="Inhalt"
                  minRows={5}
                  value={rolePageBody}
                  onChange={(e) => setRolePageBody(e.currentTarget.value)}
                  placeholder="Inhalt wie in der Word-Vorlage einfügen..."
                />
                <MultiSelect
                  label="Rollen (optional)"
                  data={roleOptions}
                  value={rolePageRoleIds}
                  onChange={setRolePageRoleIds}
                  searchable
                  clearable
                />
                <Group grow>
                  <TextInput
                    label="Sortierung"
                    value={rolePageSort}
                    onChange={(e) => setRolePageSort(e.currentTarget.value)}
                  />
                  <Switch
                    checked={rolePagePinned}
                    onChange={(e) => setRolePagePinned(e.currentTarget.checked)}
                    label="Anpinnen"
                  />
                  <Switch
                    checked={rolePagePublished}
                    onChange={(e) => setRolePagePublished(e.currentTarget.checked)}
                    label="Veröffentlicht"
                  />
                </Group>
                <Stack gap="xs">
                  <Text fw={600} size="sm">Bild (optional)</Text>
                  {rolePageImageUrl && (
                    <img src={rolePageImageUrl} alt="preview" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 6 }} />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        setRolePageImageUploading(true);
                        const objectPath = `feed/role-pages/${rolePageId || 'new'}/${Date.now()}_${f.name}`;
                        const { error: upErr } = await supabase.storage
                          .from('feed-attachments')
                          .upload(objectPath, f, { upsert: true });
                        if (upErr) throw upErr;
                        const pub = supabase.storage.from('feed-attachments').getPublicUrl(objectPath).data.publicUrl;
                        setRolePageImageUrl(pub);
                      } catch (err) {
                        notifications.show({ color: 'red', title: 'Upload fehlgeschlagen', message: String(err) });
                      } finally {
                        setRolePageImageUploading(false);
                      }
                    }}
                  />
                </Stack>
                <Group>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    loading={upsertRolePageM.isPending || rolePageImageUploading}
                    disabled={rolePageImageUploading}
                    onClick={() => upsertRolePageM.mutate()}
                  >
                    Speichern
                  </Button>
                  <Button
                    variant="light"
                    onClick={resetRolePageForm}
                  >
                    Zurücksetzen
                  </Button>
                </Group>
              </Stack>
            </Card>
          )}

          <Divider />

          {rolePagesQ.isLoading ? (
            <Text c="dimmed">Lade Seiten...</Text>
          ) : (rolePagesQ.data ?? []).length === 0 ? (
            <Text c="dimmed">Noch keine Rollen-Info-Seiten vorhanden.</Text>
          ) : (
            <Stack gap="xs">
              {(rolePagesQ.data ?? []).map((page) => (
                <Card key={page.id} withBorder radius="md" padding="sm" className="contrast-surface">
                  <Stack gap={6}>
                    <Group justify="space-between" align="flex-start">
                      <Box>
                        <Group gap={6}>
                          <Text fw={700}>{page.title || 'Info-Seite'}</Text>
                          {page.is_pinned && (
                            <Badge color="yellow" variant="light">Angepinnt</Badge>
                          )}
                          {!page.is_published && (
                            <Badge color="gray" variant="light">Entwurf</Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          Aktualisiert: {formatDate(page.updated_at)}
                        </Text>
                      </Box>
                      {canManage && (
                        <Group gap={6}>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => {
                              setRolePageId(page.id);
                              setRolePageTitle(page.title);
                              setRolePageBody(page.body);
                              setRolePageRoleIds(page.role_ids);
                              setRolePageSort(String(page.sort_order));
                              setRolePagePinned(page.is_pinned);
                              setRolePagePublished(page.is_published);
                              setRolePageImageUrl(page.primary_image_url ?? null);
                            }}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            size="xs"
                            color="red"
                            variant="light"
                            leftSection={<IconTrash size={14} />}
                            loading={deleteRolePageM.isPending}
                            onClick={() => {
                              if (!window.confirm(`Info-Seite "${page.title}" wirklich löschen?`)) return;
                              deleteRolePageM.mutate(page.id);
                            }}
                          >
                            Löschen
                          </Button>
                        </Group>
                      )}
                    </Group>
                    <Group gap={6}>
                      {(page.role_names.length === 0 ? ['Alle Rollen'] : page.role_names).map((name) => (
                        <Badge key={`${page.id}-${name}`} variant="outline" size="sm">{name}</Badge>
                      ))}
                    </Group>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {page.body}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" p="md" className="contrast-surface-soft">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={700}>Thread-Detail</Text>
            <Badge variant="light">
              {selectedThread ? selectedThread.title : 'Kein Thread gewählt'}
            </Badge>
          </Group>

          {!selectedThread ? (
            <EmptyState
              icon={<IconFile size={40} />}
              title="Thread auswählen"
              description="Wähle links einen Thread aus, um Beiträge zu verwalten."
            />
          ) : (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Stack gap="sm">
                <Text fw={600}>Neue Info posten</Text>
                <Text size="sm" c="dimmed">
                  Text schreiben, optional Dateien anhängen, auf Senden klicken.
                  Push gibt es nur bei „Anpinnen“ oder „Genehmigung anfragen“.
                </Text>
                <Textarea
                  minRows={2}
                  label="Text"
                  placeholder="Was soll die Gruppe wissen?"
                  value={postBody}
                  onChange={(e) => setPostBody(e.currentTarget.value)}
                />
                <TextInput
                  label="Link (optional)"
                  placeholder="https://…"
                  value={postLink}
                  onChange={(e) => setPostLink(e.currentTarget.value)}
                />
                <Group justify="space-between" align="center" wrap="wrap">
                  <Switch
                    checked={postPinned}
                    onChange={(e) => setPostPinned(e.currentTarget.checked)}
                    label="Wichtig – anpinnen (Push an Gruppe)"
                    disabled={!canManage || postRequestApproval}
                  />
                  <Switch
                    checked={postRequestApproval}
                    onChange={(e) => {
                      const next = e.currentTarget.checked;
                      setPostRequestApproval(next);
                      if (next) setPostPinned(false);
                    }}
                    label="Entscheidung anfragen (Push an Leitung)"
                  />
                  <FileButton
                    multiple
                    accept="image/*"
                    onChange={(files) => {
                      if (!files?.length) return;
                      mergePickedFiles(files);
                    }}
                  >
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconFile size={16} />}>
                        Fotos
                      </Button>
                    )}
                  </FileButton>
                  <FileButton
                    multiple
                    onChange={(files) => {
                      if (!files?.length) return;
                      mergePickedFiles(files);
                    }}
                  >
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconFile size={16} />}>
                        {postFiles.length > 0
                          ? `Dateien (${postFiles.length})`
                          : 'PDF / Dateien / Videos'}
                      </Button>
                    )}
                  </FileButton>
                </Group>
                <TextInput
                  label="Notiz zur Genehmigung (optional)"
                  value={postApprovalNote}
                  onChange={(e) => setPostApprovalNote(e.currentTarget.value)}
                  disabled={!postRequestApproval}
                />
                {postFiles.length > 0 && (
                  <Stack gap={6}>
                    {postFiles.map((file, index) => {
                      const status = postUploadStatus[uploadKeyForFile(file)] ?? { state: 'queued' as const };
                      return (
                        <Group key={`${file.name}-${file.size}-${index}`} justify="space-between" wrap="nowrap">
                          <Box style={{ minWidth: 0, flex: 1 }}>
                            <Text size="sm" truncate>{file.name}</Text>
                            <Text size="xs" c="dimmed">
                              {formatFileSize(file.size)} · {uploadLabelForState(status.state)}
                              {status.message ? ` · ${status.message}` : ''}
                            </Text>
                          </Box>
                          <Group gap={6} wrap="nowrap">
                            <Badge color={uploadColorForState(status.state)} variant="light">
                              {uploadLabelForState(status.state)}
                            </Badge>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="gray"
                              disabled={createPostM.isPending}
                              onClick={() => {
                                setPostFiles((prev) => prev.filter((_, i) => i !== index));
                                setPostUploadStatus((prev) => {
                                  const next = { ...prev };
                                  delete next[uploadKeyForFile(file)];
                                  return next;
                                });
                              }}
                            >
                              Entfernen
                            </Button>
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
                <Button
                  leftSection={<IconSend size={16} />}
                  loading={createPostM.isPending}
                  onClick={() => createPostM.mutate()}
                >
                  Beitrag senden
                </Button>
              </Stack>

              <Stack gap="sm">
                <Text fw={600}>Medien im Thread</Text>
                {mediaQ.isLoading ? (
                  <Text c="dimmed">Lade Medien...</Text>
                ) : (mediaQ.data ?? []).length === 0 ? (
                  <Text c="dimmed">Noch keine Medien.</Text>
                ) : (
                  <ScrollArea h={210}>
                    <Stack gap="xs">
                      {(mediaQ.data ?? []).map((media) => (
                        <Card key={media.attachment_id} withBorder padding="xs" radius="md">
                          <Group justify="space-between" align="flex-start">
                            <Box>
                              <Text fw={600}>{media.file_name || media.type.toUpperCase()}</Text>
                              <Text size="xs" c="dimmed">{media.author_name} · {formatDate(media.created_at)}</Text>
                            </Box>
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconFile size={14} />}
                              onClick={async () => {
                                try {
                                  await openAttachment(media);
                                } catch {
                                  notifications.show({
                                    color: 'red',
                                    title: 'Fehler',
                                    message: 'Medium konnte nicht geöffnet werden.',
                                  });
                                }
                              }}
                            >
                              Öffnen
                            </Button>
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  </ScrollArea>
                )}

                <Divider />

                <Text fw={600}>Umfragen</Text>
                <Textarea
                  minRows={2}
                  label="Neue Umfrage"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.currentTarget.value)}
                  placeholder="Frage eingeben..."
                />
                <Textarea
                  minRows={2}
                  label="Antwortoptionen (Komma oder neue Zeile)"
                  value={pollOptionsText}
                  onChange={(e) => setPollOptionsText(e.currentTarget.value)}
                  placeholder={'Option A\nOption B'}
                />
                <Switch
                  checked={pollAllowMultiple}
                  onChange={(e) => setPollAllowMultiple(e.currentTarget.checked)}
                  label="Mehrfachauswahl erlauben"
                />
                <Switch
                  checked={pollCollectVoteNote}
                  onChange={(e) => setPollCollectVoteNote(e.currentTarget.checked)}
                  label="Bestellinfos pro Abstimmung sammeln (z. B. Groesse)"
                />
                {pollCollectVoteNote && (
                  <TextInput
                    label="Feldname fuer Bestellinfos"
                    value={pollVoteNoteLabel}
                    onChange={(e) => setPollVoteNoteLabel(e.currentTarget.value)}
                    placeholder="Groesse / Hinweis"
                  />
                )}
                <Button
                  leftSection={<IconPlus size={14} />}
                  loading={createPollM.isPending}
                  onClick={() => createPollM.mutate()}
                >
                  Umfrage erstellen
                </Button>

                {pollsQ.isLoading ? (
                  <Text c="dimmed">Lade Polls...</Text>
                ) : (pollsQ.data ?? []).length === 0 ? (
                  <Text c="dimmed">Noch keine Polls in diesem Thread.</Text>
                ) : (
                  <ScrollArea h={320}>
                    <Stack gap="xs">
                      {(pollsQ.data ?? []).map((poll) => {
                        const selected = pollSelections[poll.poll_id] ?? [];
                        const isClosed = poll.is_closed;
                        return (
                          <Card key={poll.poll_id} withBorder radius="md" padding="xs" className="contrast-surface">
                            <Stack gap={6}>
                              <Group justify="space-between" align="flex-start">
                                <Box>
                                  <Text fw={700}>{poll.question}</Text>
                                  <Text size="xs" c="dimmed">
                                    {poll.author_name} · {formatDate(poll.created_at)} · {poll.voter_count} Abstimmungen
                                  </Text>
                                </Box>
                                <Group gap={6}>
                                  <Badge color={isClosed ? 'gray' : 'teal'} variant="light">
                                    {isClosed ? 'Geschlossen' : (poll.allow_multiple ? 'Multi' : 'Single')}
                                  </Badge>
                                  {poll.can_manage && (
                                    <Button
                                      size="xs"
                                      variant="light"
                                      loading={closePollM.isPending}
                                      onClick={() => closePollM.mutate({
                                        pollId: poll.poll_id,
                                        isClosed: !isClosed,
                                      })}
                                    >
                                      {isClosed ? 'Öffnen' : 'Schließen'}
                                    </Button>
                                  )}
                                </Group>
                              </Group>

                              <Stack gap={4}>
                                {poll.options.map((opt) => {
                                  const notes = opt.votes
                                    .filter((vote) => (vote.vote_note ?? '').trim().length > 0)
                                    .map((vote) => `${vote.author_name}: ${(vote.vote_note ?? '').trim()}`);
                                  return (
                                    <Box key={opt.id}>
                                      <Button
                                        size="xs"
                                        variant={selected.includes(opt.id) ? 'filled' : 'light'}
                                        color={selected.includes(opt.id) ? 'brand' : 'gray'}
                                        onClick={() => togglePollSelection(poll, opt.id)}
                                        disabled={isClosed}
                                      >
                                        {opt.option_text} ({opt.vote_count})
                                      </Button>
                                      {notes.length > 0 && (
                                        <Text size="xs" c="dimmed" mt={4}>
                                          Info: {notes.join(' · ')}
                                        </Text>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Stack>

                              {poll.collect_vote_note && (
                                <TextInput
                                  size="xs"
                                  label={`${poll.vote_note_label} (optional)`}
                                  value={pollVoteNotes[poll.poll_id] ?? ''}
                                  onChange={(e) => {
                                    const value = e.currentTarget.value;
                                    setPollVoteNotes((prev) => ({ ...prev, [poll.poll_id]: value }));
                                  }}
                                  disabled={isClosed}
                                />
                              )}

                              <Button
                                size="xs"
                                leftSection={<IconSend size={12} />}
                                loading={votePollM.isPending}
                                disabled={isClosed}
                                onClick={() => votePollM.mutate({
                                  pollId: poll.poll_id,
                                  optionIds: selected,
                                  voteNote: (pollVoteNotes[poll.poll_id] ?? '').trim() || null,
                                })}
                              >
                                Stimme speichern
                              </Button>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </ScrollArea>
                )}
              </Stack>
            </SimpleGrid>
          )}

          {selectedThread && (
            <>
              <Divider />
              <Stack gap="sm">
                <Text fw={600}>Beiträge</Text>
                {postsQ.isLoading ? (
                  <Text c="dimmed">Lade Beiträge...</Text>
                ) : (postsQ.data ?? []).length === 0 ? (
                  <Text c="dimmed">Noch keine Beiträge in diesem Thread.</Text>
                ) : (
                  <Stack gap="sm">
                    {(postsQ.data ?? []).map((post) => (
                      <Card
                        key={post.id}
                        withBorder
                        radius="md"
                        padding="sm"
                        className="contrast-surface"
                        style={{ borderColor: `var(--mantine-color-${postStatusColor(post)}-6)` }}
                      >
                        <Stack gap={8}>
                          <Group justify="space-between" align="flex-start">
                            <Box>
                              <Text fw={700}>{post.author_name}</Text>
                              <Text size="xs" c="dimmed">{formatDate(post.created_at)}</Text>
                            </Box>
                            <Group gap={6}>
                              {postStatusLabel(post) && (
                                <Badge color={postStatusColor(post)} variant="light">
                                  {postStatusLabel(post)}
                                </Badge>
                              )}
                              {post.is_pinned && (
                                <Badge color="yellow" variant="light">Angepinnt</Badge>
                              )}
                              {canManage && (
                                <Button
                                  size="xs"
                                  variant="light"
                                  leftSection={post.is_pinned ? <IconPinnedOff size={14} /> : <IconPinned size={14} />}
                                  loading={togglePinM.isPending}
                                  onClick={() => togglePinM.mutate(post)}
                                  disabled={post.approval_status !== 'approved'}
                                >
                                  {post.is_pinned ? 'Pin lösen' : 'Pinnen'}
                                </Button>
                              )}
                            </Group>
                          </Group>

                          {!!post.body && <Text>{post.body}</Text>}

                          {!!post.approval_request_note && (
                            <Text size="xs" c="dimmed">
                              Anfrage: {post.approval_request_note}
                            </Text>
                          )}

                          {!!post.approval_comment && (
                            <Text
                              size="xs"
                              c={post.approval_status === 'rejected' ? 'red' : 'teal'}
                            >
                              Entscheidung: {post.approval_comment}
                            </Text>
                          )}

                          {!!post.external_url && (
                            <Button
                              variant="subtle"
                              size="xs"
                              leftSection={<IconLink size={14} />}
                              onClick={() => window.open(post.external_url ?? '', '_blank', 'noopener,noreferrer')}
                            >
                              {post.external_url}
                            </Button>
                          )}

                          {post.attachments.length > 0 && (
                            <Group gap={6}>
                              {post.attachments.map((att) => (
                                <Button
                                  key={att.id}
                                  size="xs"
                                  variant="outline"
                                  leftSection={<IconFile size={12} />}
                                  onClick={async () => {
                                    try {
                                      await openAttachment(att);
                                    } catch {
                                      notifications.show({
                                        color: 'red',
                                        title: 'Fehler',
                                        message: 'Anhang konnte nicht geöffnet werden.',
                                      });
                                    }
                                  }}
                                >
                                  {att.file_name || att.type.toUpperCase()}
                                </Button>
                              ))}
                            </Group>
                          )}

                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant={post.my_reaction === 'like' ? 'filled' : 'light'}
                              leftSection={<IconHeart size={14} />}
                              onClick={() => toggleReactionM.mutate(post.id)}
                              loading={toggleReactionM.isPending}
                            >
                              {post.reaction_count}
                            </Button>
                            <Badge variant="light">Kommentare: {post.comment_count}</Badge>
                          </Group>

                          <Stack gap={4}>
                            {post.comments.slice(-5).map((comment) => (
                              <Box key={comment.id}>
                                <Text size="sm">
                                  <strong>{comment.author_name}:</strong> {comment.comment_text}
                                </Text>
                                <Text size="xs" c="dimmed">{formatDate(comment.created_at)}</Text>
                              </Box>
                            ))}
                          </Stack>

                          <Group align="flex-end">
                            <TextInput
                              style={{ flex: 1 }}
                              label="Kommentar"
                              value={commentDrafts[post.id] ?? ''}
                              onChange={(e) => setCommentDrafts((prev) => ({
                                ...prev,
                                [post.id]: e.currentTarget.value,
                              }))}
                            />
                            <Button
                              size="xs"
                              leftSection={<IconSend size={14} />}
                              loading={addCommentM.isPending}
                              onClick={() => addCommentM.mutate(post.id)}
                            >
                              Senden
                            </Button>
                          </Group>

                          {canManage && post.approval_status === 'pending' && (
                            <Group>
                              <TextInput
                                style={{ flex: 1 }}
                                label="Manager-Kommentar (optional)"
                                value={reviewCommentByPostId[post.id] ?? ''}
                                onChange={(e) => setReviewCommentByPostId((prev) => ({
                                  ...prev,
                                  [post.id]: e.currentTarget.value,
                                }))}
                              />
                              <Button
                                size="xs"
                                color="teal"
                                loading={reviewRequestM.isPending}
                                onClick={() => reviewRequestM.mutate({ postId: post.id, action: 'approve' })}
                              >
                                Genehmigen
                              </Button>
                              <Button
                                size="xs"
                                color="red"
                                variant="light"
                                loading={reviewRequestM.isPending}
                                onClick={() => reviewRequestM.mutate({ postId: post.id, action: 'reject' })}
                              >
                                Ablehnen
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" p="md" className="contrast-surface-soft">
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={700}>Personalisierte Kostüm-Felder</Text>
            <Badge color={canManage ? 'teal' : 'gray'} variant="light">
              {canManage ? 'Manager' : 'Nur Ansicht'}
            </Badge>
          </Group>

          {!canManage ? (
            <Text c="dimmed" size="sm">
              Feld-Management ist nur für Admins/Kostüm-Manager verfügbar.
            </Text>
          ) : (
            <>
              <Card withBorder radius="md" padding="sm" className="contrast-surface">
                <Stack gap="sm">
                  <Text fw={600}>ICH pro Person bearbeiten</Text>
                  <Select
                    label="Nach Rolle filtern"
                    data={[
                      { value: '', label: 'Alle Personen' },
                      ...(rolesQ.data ?? []).map((r) => ({ value: r.id, label: r.name })),
                    ]}
                    value={profileRoleFilter || null}
                    onChange={(value) => {
                      setProfileRoleFilter(value || '');
                      if (value) {
                        const stillValid = (profileUsersQ.data ?? []).some(
                          (u) => u.user_id === selectedProfileUserId && u.role_ids.includes(value),
                        );
                        if (!stillValid) setSelectedProfileUserId('');
                      }
                    }}
                    clearable
                    placeholder="Alle Personen"
                  />
                  <Select
                    label="Person"
                    data={profileUserOptions}
                    value={selectedProfileUserId || null}
                    onChange={(value) => setSelectedProfileUserId(value || '')}
                    placeholder="Person wählen"
                    searchable
                    nothingFoundMessage="Keine Person gefunden"
                  />
                  {selectedProfileUser && (
                    <Group gap={6}>
                      {(selectedProfileUser.role_names.length === 0 ? ['Ohne Rollen'] : selectedProfileUser.role_names).map((name) => (
                        <Badge key={`${selectedProfileUser.user_id}-${name}`} variant="outline" size="sm">{name}</Badge>
                      ))}
                    </Group>
                  )}
                  {managedProfileQ.isLoading ? (
                    <Text c="dimmed">Lade ICH-Felder...</Text>
                  ) : (managedProfileQ.data?.fields ?? []).length === 0 ? (
                    <Text c="dimmed">Für diese Person sind aktuell keine Felder sichtbar.</Text>
                  ) : (
                    <Stack gap="xs">
                      {(managedProfileQ.data?.fields ?? []).map((field) => {
                        const roleHint = field.role_names.join(', ');
                        const label = roleHint.trim().length === 0
                          ? field.label
                          : `${field.label} (${roleHint})`;
                        const value = managedProfileValues[field.id] ?? '';

                        if (field.field_type === 'select') {
                          const data = field.options.map((option) => ({ value: option, label: option }));
                          return (
                            <Select
                              key={field.id}
                              label={label}
                              data={data}
                              value={value || null}
                              onChange={(next) => setManagedProfileValues((prev) => ({
                                ...prev,
                                [field.id]: next ?? '',
                              }))}
                              clearable
                              searchable
                            />
                          );
                        }

                        if (field.field_type === 'multiline') {
                          return (
                            <Textarea
                              key={field.id}
                              label={label}
                              minRows={2}
                              value={value}
                              onChange={(e) => setManagedProfileValues((prev) => ({
                                ...prev,
                                [field.id]: e.currentTarget.value,
                              }))}
                            />
                          );
                        }

                        return (
                          <TextInput
                            key={field.id}
                            label={label}
                            value={value}
                            onChange={(e) => setManagedProfileValues((prev) => ({
                              ...prev,
                              [field.id]: e.currentTarget.value,
                            }))}
                          />
                        );
                      })}
                      <Button
                        leftSection={<IconCheck size={16} />}
                        loading={saveManagedProfileM.isPending}
                        onClick={() => saveManagedProfileM.mutate()}
                      >
                        ICH-Werte speichern
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>

              {/* ── ICH-Felder Auswertung ──────────────────────────────── */}
              <Card withBorder radius="md" padding="sm" className="contrast-surface">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={600}>ICH-Felder Auswertung</Text>
                    {overviewQ.data && (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconDownload size={14} />}
                        onClick={() => {
                          const csv = _buildProfileCsv(overviewQ.data!, overviewRoleFilter);
                          _downloadCsv(
                            csv,
                            `kostuem-ich-felder-${new Date().toISOString().slice(0, 10)}.csv`,
                          );
                        }}
                      >
                        CSV Export
                      </Button>
                    )}
                  </Group>

                  <Select
                    label="Nach Rolle filtern"
                    data={[
                      { value: '', label: 'Alle Rollen' },
                      ...Array.from(overviewAllRoles.entries()).map(([id, name]) => ({
                        value: id,
                        label: name,
                      })),
                    ]}
                    value={overviewRoleFilter || null}
                    onChange={(value) => setOverviewRoleFilter(value || '')}
                    clearable
                    placeholder="Alle Rollen"
                  />

                  {overviewQ.isLoading ? (
                    <Text c="dimmed" size="sm">
                      Lade Auswertung...
                    </Text>
                  ) : filteredOverviewUsers.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      Keine Personen gefunden.
                    </Text>
                  ) : (
                    <Stack gap={6}>
                      {filteredOverviewUsers.map((user) => {
                        const allFields = overviewQ.data?.fields ?? [];
                        const filledCount = allFields.filter((f) =>
                          user.values[f.id]?.trim(),
                        ).length;
                        const total = allFields.length;
                        const missingRequired = allFields.filter(
                          (f) => f.is_required && !user.values[f.id]?.trim(),
                        );
                        const complete = filledCount === total;
                        const statusColor = complete
                          ? 'teal'
                          : missingRequired.length > 0
                            ? 'red'
                            : 'yellow';
                        return (
                          <Card
                            key={user.user_id}
                            withBorder
                            radius="sm"
                            padding="xs"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedProfileUserId(user.user_id)}
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={600} truncate>
                                  {user.display_name}
                                </Text>
                                {user.role_names.length > 0 && (
                                  <Group gap={4}>
                                    {user.role_names.map((n) => (
                                      <Badge key={n} size="xs" variant="outline">
                                        {n}
                                      </Badge>
                                    ))}
                                  </Group>
                                )}
                                {missingRequired.length > 0 && (
                                  <Text size="xs" c="red">
                                    Fehlt: {missingRequired.map((f) => f.label).join(', ')}
                                  </Text>
                                )}
                              </Stack>
                              <Badge
                                color={statusColor}
                                size="sm"
                                variant="light"
                                style={{ flexShrink: 0 }}
                              >
                                {filledCount}/{total}
                              </Badge>
                            </Group>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Card>

              <Card withBorder radius="md" padding="sm" className="contrast-surface">
                <Stack gap="sm">
                  <Text fw={600}>{fieldId ? 'Feld bearbeiten' : 'Neues Feld'}</Text>
                  <TextInput
                    label="Label"
                    value={fieldLabel}
                    onChange={(e) => setFieldLabel(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Key (optional)"
                    value={fieldKey}
                    onChange={(e) => setFieldKey(e.currentTarget.value)}
                    placeholder="z.B. kragenweite"
                  />
                  <Group grow>
                    <MultiSelect
                      label="Typ"
                      data={[
                        { value: 'text', label: 'Text' },
                        { value: 'number', label: 'Zahl' },
                        { value: 'select', label: 'Auswahl' },
                        { value: 'multiline', label: 'Mehrzeilig' },
                      ]}
                      value={[fieldType]}
                      onChange={(value) => {
                        const next = value[0];
                        if (
                          next === 'text' ||
                          next === 'number' ||
                          next === 'select' ||
                          next === 'multiline'
                        ) {
                          setFieldType(next);
                        }
                      }}
                      maxValues={1}
                      searchable={false}
                    />
                    <TextInput
                      label="Sortierung"
                      value={fieldSort}
                      onChange={(e) => setFieldSort(e.currentTarget.value)}
                    />
                  </Group>
                  <TextInput
                    label="Optionen (nur bei Auswahl, Komma-getrennt)"
                    value={fieldOptions}
                    onChange={(e) => setFieldOptions(e.currentTarget.value)}
                    disabled={fieldType !== 'select'}
                  />
                  <MultiSelect
                    label="Rollen (optional)"
                    data={roleOptions}
                    value={fieldRoleIds}
                    onChange={setFieldRoleIds}
                    searchable
                    clearable
                  />
                  <Switch
                    checked={fieldRequired}
                    onChange={(e) => setFieldRequired(e.currentTarget.checked)}
                    label="Pflichtfeld"
                  />
                  <Group>
                    <Button
                      leftSection={<IconCheck size={16} />}
                      loading={upsertFieldM.isPending}
                      onClick={() => upsertFieldM.mutate()}
                    >
                      Speichern
                    </Button>
                    <Button
                      variant="light"
                      onClick={() => {
                        setFieldId('');
                        setFieldKey('');
                        setFieldLabel('');
                        setFieldType('text');
                        setFieldOptions('');
                        setFieldRequired(false);
                        setFieldSort('0');
                        setFieldRoleIds([]);
                      }}
                    >
                      Zurücksetzen
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </>
          )}

          <Divider />

          {profileQ.isLoading ? (
            <Text c="dimmed">Lade Felder...</Text>
          ) : profileQ.data && profileQ.data.fields.length > 0 ? (
            <Stack gap="xs">
              {profileQ.data.fields.map((field) => (
                <Card key={field.id} withBorder radius="md" padding="sm" className="contrast-surface">
                  <Group justify="space-between" align="flex-start">
                    <Box>
                      <Text fw={700}>{field.label}</Text>
                      <Text size="xs" c="dimmed">
                        Key: {field.field_key || '-'} · Typ: {field.field_type} · Sort: {field.sort_order}
                      </Text>
                      {field.role_names.length > 0 && (
                        <Group gap={6} mt={6}>
                          {field.role_names.map((name) => (
                            <Badge key={name} variant="outline" size="sm">{name}</Badge>
                          ))}
                        </Group>
                      )}
                      {field.field_type === 'select' && field.options.length > 0 && (
                        <Text size="xs" c="dimmed" mt={6}>
                          Optionen: {field.options.join(', ')}
                        </Text>
                      )}
                    </Box>
                    {canManage && (
                      <Group gap={6}>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => {
                            setFieldId(field.id);
                            setFieldKey(field.field_key || '');
                            setFieldLabel(field.label);
                            if (
                              field.field_type === 'text' ||
                              field.field_type === 'number' ||
                              field.field_type === 'select' ||
                              field.field_type === 'multiline'
                            ) {
                              setFieldType(field.field_type);
                            }
                            setFieldOptions(field.options.join(', '));
                            setFieldRequired(field.is_required);
                            setFieldSort(String(field.sort_order));
                            setFieldRoleIds(field.role_ids);
                          }}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconTrash size={14} />}
                          loading={deleteFieldM.isPending}
                          onClick={() => deleteFieldM.mutate(field.id)}
                        >
                          Löschen
                        </Button>
                      </Group>
                    )}
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : (
            <EmptyState
              icon={<IconFile size={40} />}
              title="Keine Felder"
              description="Lege das erste personalisierte Feld an."
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
